import { useState } from "react";
import type { BundleInstallPreview } from "@dayvise/client-sdk";
import { Badge, Button, Card, EmptyState, MUTED_TEXT, Spinner, TextField, stackStyle } from "@dayvise/react-ui";
import { useSkillBundleMutations, useSkillBundles, useSkills } from "./hooks.ts";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

/**
 * Skill bundles group owned private skills for explicit, scoped sharing.
 * Creating a bundle stores it; installing one writes its skills back into the
 * workspace through the same restore path snapshots use. Nothing is shared
 * without a deliberate action.
 */
export function SkillBundlesPanel() {
  const query = useSkillBundles();
  const skills = useSkills();
  const mutations = useSkillBundleMutations();
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [review, setReview] = useState<{ bundleId: string; preview: BundleInstallPreview } | null>(null);
  const bundles = query.data?.bundles ?? [];
  const ownedPrivate = (skills.data?.skills ?? []).filter(
    (skill) => skill.visibility === "private",
  );
  const error = query.error ?? mutations.create.error ?? mutations.preview.error ?? mutations.install.error ?? mutations.remove.error;
  const installResult = mutations.install.data;

  const toggle = (skillName: string) => {
    setSelected((current) => (current.includes(skillName) ? current.filter((entry) => entry !== skillName) : [...current, skillName]));
  };

  return (
    <Card
      title="Skill bundles"
      subtitle="Group your private skills into a versioned bundle. Installing a bundle restores its skills into this workspace; sharing a bundle beyond you is always an explicit later action."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending ? <Spinner label="Loading bundles" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Bundle action failed"}</p> : null}
        <TextField label="Bundle name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Release tooling" />
        {ownedPrivate.length === 0 ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>You have no private skills to bundle yet. Create skills first; workspace-shared skills are never bundled.</p>
        ) : (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            {ownedPrivate.map((skill) => (
              <label key={skill.skillId} style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selected.includes(skill.name)}
                  onChange={() => toggle(skill.name)}
                />
                <span>
                  {skill.name} <span style={MUTED_TEXT}>v{skill.version}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <div>
          <Button
            onClick={() => {
              mutations.create.mutate(
                { name: name.trim(), skillNames: selected },
                {
                  onSuccess: () => {
                    setName("");
                    setSelected([]);
                  },
                },
              );
            }}
            disabled={mutations.create.isPending || name.trim().length === 0 || selected.length === 0}
          >
            {mutations.create.isPending ? "Creating…" : "Create bundle"}
          </Button>
        </div>
        {!query.isPending && bundles.length === 0 ? (
          <EmptyState title="No bundles yet" description="Select private skills above to capture them as a restorable bundle." />
        ) : null}
        {bundles.map((bundle) => (
          <div key={bundle.bundleId} style={stackStyle("var(--dayvise-space-xs)")}>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
              <strong>{bundle.name}</strong>
              <Badge tone="neutral">{bundle.skills} skill(s)</Badge>
              <Badge tone="neutral">{formatBytes(bundle.bytes)}</Badge>
              <Badge tone="neutral">{bundle.createdAt.slice(0, 10)}</Badge>
            </div>
            <p style={{ ...MUTED_TEXT, margin: 0 }}>{bundle.skillNames.join(", ")}</p>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
              <Button onClick={() => mutations.preview.mutate(bundle.bundleId, { onSuccess: result => setReview({ bundleId: bundle.bundleId, preview: result.preview }) })} disabled={mutations.preview.isPending || mutations.install.isPending}>
                {mutations.preview.isPending && mutations.preview.variables === bundle.bundleId ? "Preparing review…" : "Review install"}
              </Button>
              <Button onClick={() => mutations.remove.mutate(bundle.bundleId)} disabled={mutations.remove.isPending}>
                Delete
              </Button>
            </div>
            {review?.bundleId === bundle.bundleId ? (
              <div role="group" aria-label={`Review ${bundle.name} installation`} style={stackStyle("var(--dayvise-space-xs)")}>
                <p style={{ ...MUTED_TEXT, margin: 0 }}>Will create: {review.preview.created.join(", ") || "none"}. Will replace: {review.preview.updated.join(", ") || "none"}. Existing required tools and secrets are preserved; new skills receive no grants.</p>
                <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                  <Button disabled={mutations.install.isPending} onClick={() => mutations.install.mutate({ bundleId: bundle.bundleId, expectedVersions: review.preview.expectedVersions }, { onSuccess: () => setReview(null) })}>Confirm install</Button>
                  <Button variant="ghost" onClick={() => setReview(null)}>Cancel</Button>
                </div>
              </div>
            ) : null}
            {installResult && installResult.bundleId === bundle.bundleId ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                Installed. Created: {installResult.preview.created.length > 0 ? installResult.preview.created.join(", ") : "none"} · Updated: {installResult.preview.updated.length > 0 ? installResult.preview.updated.join(", ") : "none"}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
